import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaLaptop, FaMobileAlt, FaUserCog, FaUsers, FaSync, FaSearch, FaSignOutAlt, FaSitemap } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authFetch } from '../utils/api';

// Helper function to format date
const formatDate = (dateString) => {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

// Helper function to detect device type from user agent
const detectDeviceType = (userAgent = '') => {
  if (!userAgent) return 'Unknown Device';

  userAgent = userAgent.toLowerCase();

  if (userAgent.includes('mobile') || userAgent.includes('iphone') || userAgent.includes('android')) {
    return 'Mobile Device';
  }
  if (userAgent.includes('windows')) return 'Windows PC';
  if (userAgent.includes('mac')) return 'Mac';
  if (userAgent.includes('linux')) return 'Linux PC';
  return 'Desktop';
};

// Helper function to detect browser
const detectBrowser = (userAgent = '') => {
  if (!userAgent) return 'Unknown Browser';

  userAgent = userAgent.toLowerCase();

  if (userAgent.includes('chrome')) return 'Chrome';
  if (userAgent.includes('firefox')) return 'Firefox';
  if (userAgent.includes('safari')) return 'Safari';
  if (userAgent.includes('edge')) return 'Edge';
  if (userAgent.includes('opera')) return 'Opera';
  return 'Browser';
};

export default function DeviceManagement() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // UI-only: which device (if any) is pending a sign-out confirmation in the modal below.
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  // Fetch devices data
  const fetchDevices = async () => {
    setLoading(true);
    try {
      const response = await authFetch('https://dev.sliitleo.org/Backend/get_login_logs.php');
      const data = await response.json();

      if (data.success) {
        const processedDevices = data.data.map(device => ({
          ...device,
          deviceType: detectDeviceType(device.userAgent),
          browser: detectBrowser(device.userAgent),
          formattedDate: formatDate(device.loginTime)
        }));

        setDevices(processedDevices);
      } else {
        throw new Error(data.error || 'Failed to fetch devices');
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast.error('Failed to load device data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  // Filter devices based on search query
  const filteredDevices = devices.filter(device =>
    device.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.deviceType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.browser?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.ipAddress?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleRefresh = () => {
    fetchDevices();
    toast.success('Device list refreshed');
  };

  // Confirmation now happens via the themed modal below (see confirmTarget) instead
  // of window.confirm; the network call/response handling here is unchanged.
  const handleLogoutDevice = async (sessionId) => {
    try {
      const response = await authFetch('https://dev.sliitleo.org/Backend/logout_device.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Device signed out successfully');
        fetchDevices(); // Refresh the device list
      } else {
        throw new Error(result.error || 'Failed to sign out device');
      }
    } catch (error) {
      console.error('Error signing out device:', error);
      toast.error(error.message || 'Failed to sign out device');
    }
  };

  const requestSignOut = (device) => setConfirmTarget(device);
  const cancelSignOut = () => setConfirmTarget(null);
  const confirmSignOut = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setConfirmTarget(null);
    await handleLogoutDevice(target.logId);
  };

  return (
    <>
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 ml-0 px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="page-head">
          <div>
            <h1>Device Management</h1>
            <p className="page-desc">Devices and browsers that have logged into the club portal.</p>
          </div>
        </div>

        <div className="filter-row" style={{ flexWrap: 'wrap', marginBottom: 'var(--sp-5)' }}>
          <Link to="/admin" className="chip">
            <FaUserCog size={12} /> Role Management
          </Link>
          <Link to="/session-management" className="chip">
            <FaUsers size={12} /> Session Management
          </Link>
          <Link to="/device-management" className="chip active">
            <FaLaptop size={12} /> Device Management
          </Link>
          <Link to="/path-management" className="chip">
            <FaSitemap size={12} /> Role Path Management
          </Link>
        </div>

        {/* Device List */}
        <section className="panel">
          <div className="section-head" style={{ flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
            <h2>Device Sessions</h2>
            <div className="filter-row" style={{ flexWrap: 'wrap' }}>
              <div className="relative" style={{ position: 'relative' }}>
                <FaSearch
                  size={12}
                  style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }}
                />
                <input
                  type="search"
                  aria-label="Search devices"
                  style={{ paddingLeft: 30, width: 200 }}
                  placeholder="Search devices…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                onClick={handleRefresh}
                className="btn btn-ghost btn-sm"
                disabled={loading}
                aria-label="Refresh device list"
              >
                {loading ? <span className="spinner" /> : <FaSync size={12} />}
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="empty-state">
              <span className="spinner lg" />
              <p>Loading devices…</p>
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FaLaptop /></div>
              <h3>No devices found</h3>
              <p>{searchQuery ? 'No devices match your search.' : 'Devices will appear here once members log in.'}</p>
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="btn btn-ghost btn-sm">
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Device</th>
                    <th>IP Address</th>
                    <th>Logins</th>
                    <th>Last Login</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map((device, index) => (
                    <tr key={`${device.logId}-${index}`}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            style={{
                              width: 32, height: 32, borderRadius: 'var(--radius-sm)', flexShrink: 0,
                              background: 'var(--surface-2)', display: 'grid', placeItems: 'center', color: 'var(--accent)'
                            }}
                          >
                            {device.userAgent?.toLowerCase().includes('mobile') ? <FaMobileAlt size={14} /> : <FaLaptop size={14} />}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="truncate" style={{ fontWeight: 600, maxWidth: 160 }} title={device.fullName || 'Unknown User'}>
                              {device.fullName || 'Unknown User'}
                            </div>
                            <div className="truncate" style={{ fontSize: 11.5, color: 'var(--text-3)', maxWidth: 160 }} title={device.userEmail}>
                              {device.userEmail}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>{device.deviceType}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{device.browser}</div>
                        {device.userAgent && (
                          <div className="truncate" style={{ fontSize: 10.5, color: 'var(--text-3)', maxWidth: 220 }} title={device.userAgent}>
                            {device.userAgent}
                          </div>
                        )}
                      </td>
                      <td className="num">
                        {device.ipAddress ? device.ipAddress : <span style={{ color: 'var(--text-3)' }}>Not available</span>}
                      </td>
                      <td>{device.loginCount || 1}</td>
                      <td style={{ color: 'var(--text-2)' }}>{formatDate(device.lastActivity || device.loginTime)}</td>
                      <td>
                        <div className="row-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            style={{ color: 'var(--error)' }}
                            onClick={() => requestSignOut(device)}
                            aria-label={`Sign out ${device.fullName || 'this device'}`}
                            title="Sign out this device"
                          >
                            <FaSignOutAlt size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {confirmTarget && (
        <div className="modal-overlay" onClick={cancelSignOut}>
          <div className="modal-panel panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Sign out this device?</h3>
              <button className="icon-btn" onClick={cancelSignOut} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
                <strong style={{ color: 'var(--text-1)' }}>{confirmTarget.fullName || 'This user'}</strong> will be
                signed out of {confirmTarget.deviceType || 'this device'}
                {confirmTarget.browser ? ` (${confirmTarget.browser})` : ''}. They will need to log in again to continue.
              </p>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost btn-sm" onClick={cancelSignOut}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={confirmSignOut}>Sign Out Device</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
